import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Save, Eye, Archive, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import ImageUploadField from '../../../components/admin/ImageUploadField';

type ParcoursStatus = 'draft' | 'published' | 'archived';

interface ParcoursForm {
  company_name: string;
  slug: string;
  tagline: string;
  description: string;
  sector: string;
  location: string;
  employee_count: string;
  company_logo: string;
  company_cover: string;
  meta_title: string;
  meta_description: string;
  og_image: string;
  status: ParcoursStatus;
  featured: boolean;
  published_at: string | null;
}

interface ParcoursMeta {
  created_at: string;
  updated_at: string;
}

const EMPTY_FORM: ParcoursForm = {
  company_name: '',
  slug: '',
  tagline: '',
  description: '',
  sector: '',
  location: '',
  employee_count: '',
  company_logo: '',
  company_cover: '',
  meta_title: '',
  meta_description: '',
  og_image: '',
  status: 'draft',
  featured: false,
  published_at: null,
};

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function nullifyForm(f: ParcoursForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    out[k] = v === '' ? null : v;
  }
  return out;
}

function fmtDatetime(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

export default function EditParcoursAdmin() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState<ParcoursForm>(EMPTY_FORM);
  const [meta, setMeta] = useState<ParcoursMeta | null>(null);
  const [stepCount, setStepCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }
      const { data: userData } = await supabase
        .from('users').select('role').eq('id', user.id).maybeSingle();
      if (userData?.role !== 'admin' && userData?.role !== 'editor') {
        navigate('/'); return;
      }

      if (!isNew && id) {
        try {
          const { data, error } = await supabase
            .from('parcours')
            .select('*')
            .eq('id', id)
            .maybeSingle();

          if (error) throw error;
          if (!data) { setNotFound(true); setLoading(false); return; }

          setForm({
            company_name: data.company_name ?? '',
            slug: data.slug ?? '',
            tagline: data.tagline ?? '',
            description: data.description ?? '',
            sector: data.sector ?? '',
            location: data.location ?? '',
            employee_count: data.employee_count ?? '',
            company_logo: data.company_logo ?? '',
            company_cover: data.company_cover ?? '',
            meta_title: data.meta_title ?? '',
            meta_description: data.meta_description ?? '',
            og_image: data.og_image ?? '',
            status: data.status ?? 'draft',
            featured: data.featured ?? false,
            published_at: data.published_at ?? null,
          });
          setMeta({ created_at: data.created_at, updated_at: data.updated_at });
          setSlugManuallyEdited(true);

          // Compter les étapes liées
          const { count } = await supabase
            .from('parcours_steps')
            .select('id', { count: 'exact', head: true })
            .eq('parcours_id', id);
          setStepCount(count ?? 0);
        } catch {
          toast.error('Impossible de charger le parcours.');
          navigate('/admin/parcours');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    init();
    // user?.id (et pas user) pour ne pas re-fetch quand Supabase recrée
    // l'objet user à chaque token refresh / changement de visibilité d'onglet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, id, isNew]);

  function set<K extends keyof ParcoursForm>(field: K, value: ParcoursForm[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleNameChange(value: string) {
    set('company_name', value);
    if (!slugManuallyEdited) {
      set('slug', generateSlug(value));
    }
  }

  async function handleSave(targetStatus?: ParcoursStatus) {
    const finalStatus = targetStatus ?? form.status;
    const isPublishing = finalStatus === 'published';

    if (!form.company_name.trim()) { toast.error('Le nom de l\'entreprise est obligatoire.'); return; }
    if (!form.slug.trim()) { toast.error('Le slug est obligatoire.'); return; }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) {
      toast.error('Le slug ne doit contenir que des lettres minuscules, chiffres et tirets.'); return;
    }

    const payload = nullifyForm({
      ...form,
      status: finalStatus,
      published_at: isPublishing && !form.published_at
        ? new Date().toISOString()
        : form.published_at,
    });

    const toastId = toast.loading('Enregistrement...');
    setSaving(true);
    try {
      if (isNew) {
        const { data, error } = await supabase
          .from('parcours')
          .insert(payload)
          .select('id')
          .single();

        if (error) throw error;
        toast.success('Parcours créé !', { id: toastId });
        navigate(`/admin/parcours/${data.id}/edit`);
      } else {
        const { error } = await supabase
          .from('parcours')
          .update(payload)
          .eq('id', id!);

        if (error) throw error;

        // Mettre à jour published_at local si on vient de publier
        if (isPublishing && !form.published_at) {
          setForm((prev) => ({ ...prev, published_at: payload.published_at as string }));
        }
        setForm((prev) => ({ ...prev, status: finalStatus }));
        toast.success('Parcours mis à jour !', { id: toastId });
      }
    } catch (err: unknown) {
      const pg = err as { code?: string; message?: string };
      if (pg?.code === '23505') {
        toast.error('Ce slug est déjà utilisé. Choisissez-en un autre.', { id: toastId });
      } else {
        toast.error(pg?.message ?? 'Erreur lors de la sauvegarde.', { id: toastId });
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive() {
    if (!id || isNew) return;
    if (!confirm('Archiver ce parcours ?')) return;
    const toastId = toast.loading('Archivage...');
    try {
      const { error } = await supabase
        .from('parcours').update({ status: 'archived' }).eq('id', id);
      if (error) throw error;
      toast.success('Parcours archivé.', { id: toastId });
      navigate('/admin/parcours');
    } catch {
      toast.error('Impossible d\'archiver.', { id: toastId });
    }
  }

  // ── États ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <p className="text-5xl font-bold text-slate-200 mb-4">404</p>
          <h1 className="text-2xl font-semibold text-slate-800 mb-2">Parcours introuvable</h1>
          <p className="text-slate-500 mb-8">Ce parcours n'existe pas ou a été supprimé.</p>
          <Link
            to="/admin/parcours"
            className="inline-flex items-center gap-2 text-sm font-medium text-pink-600 hover:text-pink-700"
          >
            <ChevronLeft className="h-4 w-4" /> Retour à la liste
          </Link>
        </div>
      </div>
    );
  }

  const metaTitleLen = form.meta_title.length;
  const metaDescLen = form.meta_description.length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      <div className="mx-auto max-w-5xl px-6 py-8">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/parcours"
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {isNew ? 'Nouveau parcours' : 'Modifier le parcours'}
              </h1>
              {!isNew && form.slug && (
                <p className="text-xs text-slate-500 mt-0.5">/decouvrir/{form.slug}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!isNew && form.status !== 'archived' && (
              <button
                onClick={handleArchive}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Archive className="h-4 w-4" /> Archiver
              </button>
            )}
            {!isNew && (
              <Link
                to={`/admin/parcours/${id}/steps`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Layers className="h-4 w-4" /> Étapes
              </Link>
            )}
            {!isNew && form.status === 'published' && (
              <a
                href={`/decouvrir/${form.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <Eye className="h-4 w-4" /> Voir
              </a>
            )}
            <button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> Brouillon
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-pink-600 px-4 py-2 text-sm font-semibold text-white hover:bg-pink-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Publier'}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_300px]">

          {/* Colonne principale */}
          <div className="space-y-6">

            {/* Informations entreprise */}
            <Section title="Informations entreprise">
              <Field label="Nom de l'entreprise *">
                <input
                  value={form.company_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="ex : Cabinet Coulaud"
                  className={inputClass}
                />
              </Field>
              <Field label="Slug *" hint="Généré automatiquement, modifiable">
                <input
                  value={form.slug}
                  onChange={(e) => { set('slug', e.target.value); setSlugManuallyEdited(true); }}
                  placeholder="cabinet-coulaud"
                  className={inputClass}
                />
                <p className="mt-1 text-xs text-slate-400">
                  URL publique : recruteurs.io/decouvrir/<strong>{form.slug || 'slug'}</strong>
                </p>
              </Field>
              <Field label="Accroche">
                <input
                  value={form.tagline}
                  onChange={(e) => set('tagline', e.target.value)}
                  placeholder="ex : Cabinet de recrutement spécialisé en RH"
                  className={inputClass}
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={form.description}
                  onChange={(e) => set('description', e.target.value)}
                  rows={5}
                  placeholder="Présentation de l'entreprise, sa mission, ses valeurs..."
                  className={`${inputClass} resize-y`}
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Secteur">
                  <input
                    value={form.sector}
                    onChange={(e) => set('sector', e.target.value)}
                    placeholder="ex : Conseil en RH"
                    className={inputClass}
                  />
                </Field>
                <Field label="Localisation">
                  <input
                    value={form.location}
                    onChange={(e) => set('location', e.target.value)}
                    placeholder="ex : Marseille, France"
                    className={inputClass}
                  />
                </Field>
                <Field label="Taille de l'entreprise">
                  <input
                    value={form.employee_count}
                    onChange={(e) => set('employee_count', e.target.value)}
                    placeholder="ex : 10-50 personnes"
                    className={inputClass}
                  />
                </Field>
              </div>
            </Section>

            {/* Visuels */}
            <Section title="Visuels">
              <ImageUploadField
                label="Logo de l'entreprise"
                recommended="Recommandé : carré, fond transparent"
                value={form.company_logo}
                onChange={(url) => set('company_logo', url)}
                bucket="parcours-assets"
                folder="parcours/logos"
                slug={form.slug || undefined}
                previewClass="h-16 w-auto max-w-[120px] object-contain p-2"
                disabled={saving}
              />
              <ImageUploadField
                label="Image de couverture"
                recommended="Recommandé : 1200 × 400 px"
                value={form.company_cover}
                onChange={(url) => set('company_cover', url)}
                bucket="parcours-assets"
                folder="parcours/covers"
                slug={form.slug || undefined}
                previewClass="h-28 w-full object-cover"
                disabled={saving}
              />
            </Section>

            {/* SEO */}
            <Section title="SEO">
              <Field label="Meta titre" hint={metaTitleLen > 70 ? `${metaTitleLen}/70 — trop long` : `${metaTitleLen}/70`}>
                <input
                  value={form.meta_title}
                  onChange={(e) => set('meta_title', e.target.value)}
                  placeholder={form.company_name ? `${form.company_name} — Parcours de découverte | recruteurs.io` : 'Laissez vide pour générer automatiquement'}
                  className={`${inputClass} ${metaTitleLen > 70 ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-400' : ''}`}
                />
                {metaTitleLen > 70 && (
                  <p className="mt-1 text-xs text-amber-500">Recommandé : 70 caractères maximum pour Google.</p>
                )}
              </Field>
              <Field label="Meta description" hint={metaDescLen > 160 ? `${metaDescLen}/160 — trop long` : `${metaDescLen}/160`}>
                <textarea
                  value={form.meta_description}
                  onChange={(e) => set('meta_description', e.target.value)}
                  rows={3}
                  placeholder="Description courte pour les moteurs de recherche..."
                  className={`${inputClass} resize-none ${metaDescLen > 160 ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-400' : ''}`}
                />
                {metaDescLen > 160 && (
                  <p className="mt-1 text-xs text-amber-500">Recommandé : 160 caractères maximum pour Google.</p>
                )}
              </Field>
              <Field label="Image Open Graph">
                <input
                  value={form.og_image}
                  onChange={(e) => set('og_image', e.target.value)}
                  placeholder="https://... (1200×630px recommandé)"
                  className={inputClass}
                />
              </Field>
            </Section>

          </div>

          {/* Colonne droite */}
          <div className="space-y-5">

            {/* Publication */}
            <div className="sticky top-6 space-y-5">
              <Section title="Publication">
                <Field label="Statut">
                  <select
                    value={form.status}
                    onChange={(e) => set('status', e.target.value as ParcoursStatus)}
                    className={inputClass}
                  >
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                    <option value="archived">Archivé</option>
                  </select>
                </Field>

                <div className="flex items-center gap-3 pt-1">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={form.featured}
                    onChange={(e) => set('featured', e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-pink-600"
                  />
                  <label htmlFor="featured" className="text-sm font-medium text-slate-700">
                    Mettre en avant
                  </label>
                </div>

                {form.published_at && (
                  <p className="text-xs text-slate-500">
                    Publié le : {fmtDatetime(form.published_at)}
                  </p>
                )}

                {form.status === 'published' && !isNew && (
                  <a
                    href={`/decouvrir/${form.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 flex items-center gap-1.5 text-sm text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" /> Voir la page publique
                  </a>
                )}
              </Section>

              {/* Métadonnées techniques */}
              {meta && (
                <Section title="Informations">
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">
                      <span className="font-medium text-slate-600">Créé le </span>
                      {fmtDatetime(meta.created_at)}
                    </p>
                    <p className="text-xs text-slate-500">
                      <span className="font-medium text-slate-600">Modifié le </span>
                      {fmtDatetime(meta.updated_at)}
                    </p>
                    {id && (
                      <p className="text-xs text-slate-400 font-mono break-all">{id}</p>
                    )}
                  </div>
                </Section>
              )}

              {/* Compteur d'étapes (édition uniquement) */}
              {!isNew && stepCount !== null && (
                <Section title="Étapes">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      <span className="font-semibold text-slate-900">{stepCount}</span>{' '}
                      étape{stepCount !== 1 ? 's' : ''}
                    </span>
                    <Link
                      to={`/admin/parcours/${id}/steps`}
                      className="text-xs font-medium text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      Gérer →
                    </Link>
                  </div>
                </Section>
              )}

              {/* Actions rapides (édition uniquement) */}
              {!isNew && (
                <Section title="Actions">
                  <div className="space-y-2">
                    <Link
                      to={`/admin/parcours/${id}/steps`}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Layers className="h-4 w-4 text-slate-400" /> Gérer les étapes
                    </Link>
                    <Link
                      to={`/admin/parcours/${id}/preview`}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <Eye className="h-4 w-4 text-slate-400" /> Prévisualiser
                    </Link>
                  </div>
                </Section>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sous-composants ────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="mb-5 text-base font-bold text-slate-900">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {hint && <span className="ml-1.5 text-xs font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  'w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition focus:border-slate-900 focus:ring-1 focus:ring-slate-900';
